import React, { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Coins,
  Home,
  Loader2,
  Plus,
  Trash2,
  TrendingUp,
  Truck,
  Wallet,
  Zap,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Label } from "@/components/ui/Label";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  type Debt,
  type DebtPayoffPlan,
  type DebtType,
  type PersonalAsset,
  type PersonalAssetType,
  type PersonalIncome,
  useCreateDebt,
  useCreatePersonalAsset,
  useDebts,
  useDeleteDebt,
  useDeletePersonalAsset,
  useNetWorth,
  usePayoffPlan,
  usePersonalAssets,
  usePersonalIncome,
  useUpdateDebt,
  useUpdatePersonalAsset,
  useUpdatePersonalIncome,
} from "@/hooks/usePersonal";
import { useBlsBenchmark, useScheduleASimulation, useTaxProjection } from "@/hooks/useTax";
import { formatMoney, formatPercent } from "@/lib/utils";

const debtTypeLabel: Record<DebtType, string> = {
  Other: "Other",
  CreditCard: "Credit card",
  Vehicle: "Vehicle loan",
  StudentLoan: "Student loan",
  PersonalLoan: "Personal loan",
  Mortgage: "Mortgage",
  Heloc: "HELOC",
  Medical: "Medical",
  BuyNowPayLater: "BNPL",
};

export function PersonalPage() {
  return (
    <>
      <PageHeader
        eyebrow="Personal"
        title="Personal Hub — household income, debts, payoff plan"
        description="Monthly cash flow + every debt + side-by-side avalanche / snowball / business-convert payoff plans. The truck-to-LLC §179 play is wired here too."
      />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <PersonalIncomeCard />
        <CashFlowSummaryCard />
      </div>

      <div className="mt-6">
        <NetWorthCard />
      </div>

      <div className="mt-6">
        <AssetsCard />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
        <ScheduleACard />
        <BlsBenchmarkCard />
      </div>

      <div className="mt-6">
        <DebtsCard />
      </div>

      <div className="mt-6">
        <PayoffPlanCard />
      </div>
    </>
  );
}

// =====================================================================
// Income card
// =====================================================================
function PersonalIncomeCard() {
  const income = usePersonalIncome();
  const update = useUpdatePersonalIncome();
  const [draft, setDraft] = useState<Partial<PersonalIncome>>({});

  useEffect(() => {
    if (income.data) {
      setDraft(income.data);
    }
  }, [income.data]);

  const onChange = (key: keyof PersonalIncome) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value === "" ? null : Number(e.target.value);
    setDraft((d) => ({ ...d, [key]: Number.isNaN(v as number) ? null : v }));
  };

  const onCheckChange = (key: keyof PersonalIncome) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setDraft((d) => ({ ...d, [key]: e.target.checked }));
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-4 w-4" /> Monthly income & expenses
            </CardTitle>
            <CardDescription>
              Take-home pay and recurring expenses. Drives the surplus number that fuels the debt payoff plan.
            </CardDescription>
          </div>
          <Button
            size="sm"
            variant="primary"
            onClick={() => update.mutate(draft)}
            disabled={update.isPending}
          >
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <NumField
          label="Your monthly NET take-home (W-2)"
          help="After fed/state/FICA + 401(k) + health insurance."
          value={draft.monthlyW2NetTakeHome ?? null}
          onChange={onChange("monthlyW2NetTakeHome")}
        />
        <NumField
          label="Your monthly GROSS W-2 pay"
          help="Pre-tax. Used for marginal-rate sanity."
          value={draft.monthlyW2Gross ?? null}
          onChange={onChange("monthlyW2Gross")}
        />
        <NumField
          label="Spouse monthly NET take-home"
          help="Leave blank if spouse doesn't earn."
          value={draft.spouseMonthlyNetTakeHome ?? null}
          onChange={onChange("spouseMonthlyNetTakeHome")}
        />
        <NumField
          label="Monthly FIXED expenses"
          help="Mortgage/rent, insurance, utilities, childcare."
          value={draft.monthlyFixedExpenses ?? null}
          onChange={onChange("monthlyFixedExpenses")}
        />
        <NumField
          label="Monthly VARIABLE expenses"
          help="Groceries, gas, eating out, subscriptions."
          value={draft.monthlyVariableExpenses ?? null}
          onChange={onChange("monthlyVariableExpenses")}
        />
        <NumField
          label="Emergency fund balance"
          help="Cash + HYSA, not retirement."
          value={draft.emergencyFundBalance ?? null}
          onChange={onChange("emergencyFundBalance")}
        />
        <NumField
          label="Day-job 401(k) YTD contribution"
          help="Drives the 'max it' play."
          value={draft.employerPlanYtdContribution ?? null}
          onChange={onChange("employerPlanYtdContribution")}
        />
        <div className="space-y-2">
          <Label className="text-xs">Day-job plan features</Label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.employerOffersHdhpHsa ?? false}
              onChange={onCheckChange("employerOffersHdhpHsa")}
            />
            HDHP w/ HSA available
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.employerPlanSupportsMegaBackdoorRoth ?? false}
              onChange={onCheckChange("employerPlanSupportsMegaBackdoorRoth")}
            />
            401(k) supports Mega Backdoor Roth
          </label>
        </div>
      </CardContent>
    </Card>
  );
}

// =====================================================================
// Cash flow summary (derived)
// =====================================================================
function CashFlowSummaryCard() {
  const income = usePersonalIncome();
  const i = income.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4" /> This month at a glance
        </CardTitle>
        <CardDescription>Derived from what you entered on the left.</CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-4">
        <Stat label="Total household NET" value={formatMoney(i?.totalMonthlyNet)} />
        <Stat label="Fixed expenses" value={formatMoney(i?.monthlyFixedExpenses)} />
        <Stat label="Variable expenses" value={formatMoney(i?.monthlyVariableExpenses)} />
        <Stat
          label="Surplus (available to debt + savings)"
          value={formatMoney(i?.monthlySurplus)}
          tone={(i?.monthlySurplus ?? 0) > 0 ? "good" : "bad"}
        />
        <Stat
          label="Emergency fund target (6 months fixed)"
          value={
            i?.monthlyFixedExpenses ? formatMoney(i.monthlyFixedExpenses * 6) : "—"
          }
        />
        <Stat
          label="Emergency fund balance"
          value={formatMoney(i?.emergencyFundBalance)}
        />
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "good" | "bad" }) {
  return (
    <div className="rounded-md border border-border bg-muted/20 p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div
        className={
          tone === "good"
            ? "text-lg font-semibold text-emerald-400 tabular-nums"
            : tone === "bad"
              ? "text-lg font-semibold text-rose-400 tabular-nums"
              : "text-lg font-semibold tabular-nums"
        }
      >
        {value}
      </div>
    </div>
  );
}

function NumField({
  label,
  help,
  value,
  onChange,
}: {
  label: string;
  help?: string;
  value: number | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input
        type="number"
        inputMode="decimal"
        step="0.01"
        value={value ?? ""}
        onChange={onChange}
        placeholder="—"
      />
      {help && <p className="text-[11px] text-muted-foreground">{help}</p>}
    </div>
  );
}

// =====================================================================
// Debts grid
// =====================================================================
function DebtsCard() {
  const debts = useDebts();
  const create = useCreateDebt();
  const remove = useDeleteDebt();
  const [adding, setAdding] = useState(false);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-4 w-4" /> Debts
            </CardTitle>
            <CardDescription>
              Record every consumer/auto/student/CC balance. Mark vehicles as &quot;business-convertible&quot; to
              unlock the §179 truck-to-LLC payoff scenario.
            </CardDescription>
          </div>
          <Button size="sm" variant="primary" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" /> Add debt
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {debts.isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {debts.data && debts.data.length === 0 && !adding && (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            No debts on file. Click <span className="font-semibold">Add debt</span> to get started.
          </div>
        )}
        {debts.data?.map((d) => (
          <DebtRow key={d.id} debt={d} onDelete={() => remove.mutate(d.id)} />
        ))}
        {adding && (
          <DebtRow
            debt={emptyDebt()}
            isNew
            onCancel={() => setAdding(false)}
            onCreate={async (input) => {
              await create.mutateAsync(input);
              setAdding(false);
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function emptyDebt(): Debt {
  return {
    id: "00000000-0000-0000-0000-000000000000",
    ownerUserId: "",
    name: "",
    type: "CreditCard",
    currentBalance: 0,
    apr: 0,
    minimumMonthlyPayment: null,
    actualMonthlyPayment: null,
    originalAmount: null,
    originationDate: null,
    vehicleYear: null,
    vehicleMake: null,
    vehicleModel: null,
    vehicleGvwrLb: null,
    vehicleFmv: null,
    estimatedBusinessUsePct: null,
    isBusinessConvertible: false,
    securedByAssetId: null,
    notes: "",
  };
}

function DebtRow({
  debt,
  isNew,
  onDelete,
  onCreate,
  onCancel,
}: {
  debt: Debt;
  isNew?: boolean;
  onDelete?: () => void;
  onCreate?: (input: Partial<Debt>) => Promise<void> | void;
  onCancel?: () => void;
}) {
  const update = useUpdateDebt();
  const [draft, setDraft] = useState<Debt>(debt);
  const [expanded, setExpanded] = useState(isNew ?? false);
  const [saveFailed, setSaveFailed] = useState(false);

  useEffect(() => {
    setDraft(debt);
    setSaveFailed(false);
  }, [debt]);

  const isVehicle = draft.type === "Vehicle";
  const dirty = JSON.stringify(draft) !== JSON.stringify(debt);

  const save = async () => {
    setSaveFailed(false);
    try {
      if (isNew && onCreate) {
        await onCreate(draft);
      } else {
        await update.mutateAsync({ ...draft, id: draft.id });
      }
    } catch {
      setSaveFailed(true);
    }
  };

  return (
    <div className="rounded-md border border-border bg-muted/10 p-3">
      <div className="grid grid-cols-12 items-center gap-2">
        <div className="col-span-3">
          <Input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="e.g. Ford F-250 loan"
          />
        </div>
        <div className="col-span-2">
          <Select
            value={draft.type}
            onChange={(e) => setDraft({ ...draft, type: e.target.value as DebtType })}
          >
            {Object.entries(debtTypeLabel).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
        </div>
        <div className="col-span-2">
          <Input
            type="number"
            inputMode="decimal"
            step="0.01"
            placeholder="Balance"
            value={draft.currentBalance || ""}
            onChange={(e) => setDraft({ ...draft, currentBalance: Number(e.target.value) || 0 })}
          />
        </div>
        <div className="col-span-2">
          <Input
            type="number"
            inputMode="decimal"
            step="0.01"
            placeholder="APR %"
            value={draft.apr ? (draft.apr * 100).toFixed(2) : ""}
            onChange={(e) => setDraft({ ...draft, apr: (Number(e.target.value) || 0) / 100 })}
          />
        </div>
        <div className="col-span-2">
          <Input
            type="number"
            inputMode="decimal"
            step="0.01"
            placeholder="Min payment"
            value={draft.minimumMonthlyPayment ?? ""}
            onChange={(e) =>
              setDraft({
                ...draft,
                minimumMonthlyPayment: e.target.value === "" ? null : Number(e.target.value),
              })
            }
          />
        </div>
        <div className="col-span-1 flex justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setExpanded((x) => !x)}
            title={expanded ? "Collapse" : "More fields"}
          >
            {expanded ? "−" : "+"}
          </Button>
          {!isNew && onDelete && (
            <Button size="sm" variant="ghost" onClick={onDelete} title="Delete">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-3 grid grid-cols-1 gap-3 border-t border-border pt-3 md:grid-cols-2">
          {isVehicle && (
            <>
              <div className="md:col-span-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs">
                <div className="flex items-center gap-2 font-semibold text-emerald-300">
                  <Zap className="h-4 w-4" /> Vehicle / business-convert details
                </div>
                <p className="mt-1 text-emerald-100/80">
                  GVWR &gt; 6,000 lb unlocks the heavy-vehicle §179 cap ($31,300 in 2025). Mark
                  &quot;business-convertible&quot; to model the sell-to-LLC payoff scenario in the plan below.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <NumField
                  label="Year"
                  value={draft.vehicleYear}
                  onChange={(e) =>
                    setDraft({ ...draft, vehicleYear: e.target.value === "" ? null : Number(e.target.value) })
                  }
                />
                <div className="space-y-1">
                  <Label className="text-xs">Make</Label>
                  <Input
                    value={draft.vehicleMake ?? ""}
                    onChange={(e) => setDraft({ ...draft, vehicleMake: e.target.value || null })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Model</Label>
                  <Input
                    value={draft.vehicleModel ?? ""}
                    onChange={(e) => setDraft({ ...draft, vehicleModel: e.target.value || null })}
                  />
                </div>
              </div>
              <NumField
                label="GVWR (lb) — door-jamb sticker"
                value={draft.vehicleGvwrLb}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    vehicleGvwrLb: e.target.value === "" ? null : Number(e.target.value),
                  })
                }
              />
              <NumField
                label="Current FMV (KBB private-party)"
                value={draft.vehicleFmv}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    vehicleFmv: e.target.value === "" ? null : Number(e.target.value),
                  })
                }
              />
              <div className="space-y-1">
                <Label className="text-xs">Estimated business use % (if converted)</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="1"
                  placeholder="e.g. 80"
                  value={
                    draft.estimatedBusinessUsePct == null
                      ? ""
                      : Math.round(draft.estimatedBusinessUsePct * 100)
                  }
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      estimatedBusinessUsePct:
                        e.target.value === "" ? null : Number(e.target.value) / 100,
                    })
                  }
                />
                <p className="text-[11px] text-muted-foreground">
                  Must be &gt;50% to qualify for §179 / bonus depreciation.
                </p>
              </div>
              <label className="md:col-span-2 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.isBusinessConvertible}
                  onChange={(e) => setDraft({ ...draft, isBusinessConvertible: e.target.checked })}
                />
                Mark this vehicle as <span className="font-semibold">business-convertible</span> — surface the
                sell-to-LLC §179 play on this debt.
              </label>
            </>
          )}
          <div className="md:col-span-2 space-y-1">
            <Label className="text-xs">Notes</Label>
            <Input
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            />
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          {saveFailed && (
            <span className="text-destructive">
              Debt was not saved. Check the fields and try again.
            </span>
          )}
          {draft.isBusinessConvertible && draft.type === "Vehicle" && (
            <Badge tone="info" className="gap-1">
              <Zap className="h-3 w-3" /> Convertible
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isNew && (
            <Button size="sm" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          )}
          {(isNew || dirty) && (
            <Button size="sm" variant="primary" onClick={save}>
              {isNew ? "Add" : "Save"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Payoff plan card
// =====================================================================
function PayoffPlanCard() {
  const plan = usePayoffPlan();

  if (plan.isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Computing payoff strategies…
        </CardContent>
      </Card>
    );
  }

  if (!plan.data) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-muted-foreground">
          No data.
        </CardContent>
      </Card>
    );
  }

  const c = plan.data.comparison;
  const inputs = plan.data.inputs;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4" /> Payoff comparison
        </CardTitle>
        <CardDescription>
          Avalanche (math optimal) vs Snowball (psychological wins) vs Business-convert hybrid (where eligible).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {c.isBlocked && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
            <div className="mb-2 flex items-center gap-2 font-semibold text-amber-200">
              <AlertTriangle className="h-4 w-4" /> Need a few more inputs
            </div>
            <ul className="space-y-1 text-amber-100/80">
              {c.missingInputs.map((m, i) => (
                <li key={i}>
                  <span className="font-semibold">{m.label}:</span> {m.why}
                </li>
              ))}
            </ul>
          </div>
        )}

        {inputs.monthlyAvailableSource === "Missing" && !c.isBlocked && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
            Add <span className="font-semibold">monthly net + monthly expenses</span> above to show real payoff
            timelines. Without it, the planner can&apos;t know how much extra you can throw at debts.
          </div>
        )}

        {c.notes.length > 0 && (
          <div className="rounded-md border border-border bg-muted/30 p-3 text-xs">
            {c.notes.map((n, i) => (
              <div key={i} className={i > 0 ? "mt-2" : undefined}>
                {n}
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <PlanColumn
            title="Avalanche"
            subtitle="Highest APR first · math optimal"
            plan={c.avalanche}
            highlight={!c.businessConvertHybrid}
          />
          <PlanColumn
            title="Snowball"
            subtitle="Smallest balance first · momentum"
            plan={c.snowball}
          />
          {c.businessConvertHybrid ? (
            <PlanColumn
              title="Business-convert hybrid"
              subtitle="Sell eligible vehicle to LLC + §179"
              plan={c.businessConvertHybrid}
              highlight
              extra={
                <div className="mt-2 space-y-1 text-[11px]">
                  {c.businessConvertHybrid.section179Deduction != null && (
                    <div>
                      §179 deduction unlocked:{" "}
                      <span className="font-semibold">
                        {formatMoney(c.businessConvertHybrid.section179Deduction)}
                      </span>
                    </div>
                  )}
                  {c.businessConvertHybrid.estimatedTaxSavings != null && (
                    <div>
                      Estimated tax saved:{" "}
                      <span className="font-semibold text-emerald-300">
                        {formatMoney(c.businessConvertHybrid.estimatedTaxSavings)}
                      </span>
                    </div>
                  )}
                </div>
              }
            />
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-6 text-center text-xs text-muted-foreground">
                Mark a vehicle as <span className="font-semibold">business-convertible</span> in the debts table
                above to unlock the §179 sell-to-LLC scenario.
              </CardContent>
            </Card>
          )}
        </div>

        <div className="rounded-md border border-border bg-muted/20 p-3 text-xs">
          <span className="font-semibold">Inputs used: </span>
          monthly available {formatMoney(inputs.monthlyAvailable)} (
          {inputs.monthlyAvailableSource === "PersonalIncomeSurplus"
            ? "from your income/expense surplus"
            : inputs.monthlyAvailableSource === "Override"
              ? "manual override"
              : "MISSING — fill in income/expenses above"}
          )
          {inputs.marginalRate != null && (
            <>
              {" "}
              · marginal rate <span className="font-semibold">{formatPercent(inputs.marginalRate)}</span>
              {" "}(used to estimate §179 tax savings)
            </>
          )}
          {inputs.marginalRateBlocked && (
            <>
              {" "}
              · marginal rate <span className="font-semibold">unknown</span> —{" "}
              <Link to="/tax-intake" className="text-primary hover:underline">
                finish Tax Intake
              </Link>{" "}
              to quantify §179 savings.
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2">
          <Link to="/strategy/tax-plays">
            <Button size="sm" variant="outline">
              View Tax Plays
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

function PlanColumn({
  title,
  subtitle,
  plan,
  highlight,
  extra,
}: {
  title: string;
  subtitle: string;
  plan: DebtPayoffPlan;
  highlight?: boolean;
  extra?: React.ReactNode;
}) {
  return (
    <div
      className={
        highlight
          ? "rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3"
          : "rounded-md border border-border p-3"
      }
    >
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</div>
      <div className="mt-1 text-[11px] text-muted-foreground">{subtitle}</div>
      <div className="mt-3 space-y-2 text-sm">
        <div className="flex items-baseline justify-between">
          <span className="text-muted-foreground">Months to debt-free</span>
          <span className="text-xl font-bold tabular-nums">
            {plan.monthsToDebtFree ?? "—"}
          </span>
        </div>
        <div className="flex items-baseline justify-between">
          <span className="text-muted-foreground">Total interest paid</span>
          <span className="font-semibold tabular-nums">{formatMoney(plan.totalInterestPaid)}</span>
        </div>
        {plan.totalPaid != null && (
          <div className="flex items-baseline justify-between">
            <span className="text-muted-foreground">Total paid</span>
            <span className="tabular-nums">{formatMoney(plan.totalPaid)}</span>
          </div>
        )}
        {plan.negativeCashFlow && (
          <div className="text-xs text-amber-300">
            ⚠ Monthly available is below total minimums — plan assumes minimums are paid; the shortfall must come
            from somewhere.
          </div>
        )}
      </div>
      {plan.perDebt.length > 0 && (
        <div className="mt-3 space-y-1 border-t border-border pt-2 text-xs">
          {plan.perDebt.map((p) => (
            <div key={p.id} className="flex items-center justify-between">
              <span className="truncate">{p.name}</span>
              <span className="tabular-nums">
                {p.paidOffMonth ? (
                  <span className="text-emerald-300">
                    <CheckCircle2 className="mr-1 inline h-3 w-3" />
                    mo {p.paidOffMonth}
                  </span>
                ) : (
                  <span className="text-amber-300">unpaid &gt; cap</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
      {extra}
    </div>
  );
}

// =====================================================================
// Net Worth card (derived)
// =====================================================================
const ASSET_TYPES_TAX_ADV: PersonalAssetType[] = [
  "RothIra",
  "TraditionalIra",
  "Roth401k",
  "Traditional401k",
  "Hsa",
  "EducationSavings529",
];

const assetTypeLabel: Record<PersonalAssetType, string> = {
  Other: "Other",
  Vehicle: "Vehicle",
  PrimaryResidence: "Primary residence",
  RentalProperty: "Rental property",
  Checking: "Checking",
  Savings: "Savings / HYSA",
  Brokerage: "Brokerage (taxable)",
  Crypto: "Crypto",
  RothIra: "Roth IRA",
  TraditionalIra: "Traditional IRA",
  Roth401k: "Roth 401(k)",
  Traditional401k: "Traditional 401(k)",
  Hsa: "HSA",
  EducationSavings529: "529 (Edvest)",
  PreciousMetals: "Precious metals",
  Collectible: "Collectible",
  BusinessOwnership: "Business equity",
};

function NetWorthCard() {
  const nw = useNetWorth();

  if (nw.isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Computing net worth…
        </CardContent>
      </Card>
    );
  }

  const s = nw.data;
  if (!s || s.isEmpty) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Coins className="h-4 w-4" /> Net worth
          </CardTitle>
          <CardDescription>
            Add personal assets and debts below to track real-time household net worth, liquid net worth,
            and tax-deferred buckets.
          </CardDescription>
        </CardHeader>
        <CardContent className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No assets or debts yet. Use the cards below to add them.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Coins className="h-4 w-4" /> Net worth
        </CardTitle>
        <CardDescription>
          Net of every asset and debt you&apos;ve entered. Tax-advantaged balances are split out so
          you can see what&apos;s already pre-tax-locked.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat
            label="Net worth"
            value={formatMoney(s.netWorth)}
            tone={s.netWorth >= 0 ? "good" : "bad"}
          />
          <Stat
            label="Liquid net worth"
            value={formatMoney(s.liquidNetWorth)}
            tone={s.liquidNetWorth >= 0 ? "good" : "bad"}
          />
          <Stat label="Total assets" value={formatMoney(s.totalAssets)} />
          <Stat label="Total debts" value={formatMoney(s.totalDebts)} tone={s.totalDebts > 0 ? "bad" : undefined} />
          <Stat label="Taxable assets" value={formatMoney(s.taxableAssets)} />
          <Stat label="Tax-advantaged" value={formatMoney(s.taxAdvantagedAssets)} />
          <Stat label="Real estate" value={formatMoney(s.realEstateAssets)} />
          <Stat label="Vehicle equity" value={formatMoney(s.vehicleEquity)} />
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="rounded-md border border-border bg-muted/10 p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Assets by type
            </div>
            <div className="space-y-1 text-sm">
              {s.byAssetType.map((b) => (
                <div key={b.type} className="flex items-baseline justify-between gap-2">
                  <span className="truncate">
                    {assetTypeLabel[b.type as PersonalAssetType] ?? b.type}{" "}
                    <span className="text-[10px] text-muted-foreground">×{b.count}</span>
                  </span>
                  <span className="tabular-nums">{formatMoney(b.total)}</span>
                </div>
              ))}
              {s.byAssetType.length === 0 && (
                <div className="text-xs text-muted-foreground">No assets entered.</div>
              )}
            </div>
          </div>
          <div className="rounded-md border border-border bg-muted/10 p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Debts by type
            </div>
            <div className="space-y-1 text-sm">
              {s.byDebtType.map((b) => (
                <div key={b.type} className="flex items-baseline justify-between gap-2">
                  <span className="truncate">
                    {debtTypeLabel[b.type as DebtType] ?? b.type}{" "}
                    <span className="text-[10px] text-muted-foreground">×{b.count}</span>
                  </span>
                  <span className="tabular-nums">{formatMoney(b.total)}</span>
                </div>
              ))}
              {s.byDebtType.length === 0 && (
                <div className="text-xs text-muted-foreground">No debts entered.</div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// =====================================================================
// Personal Assets card (CRUD)
// =====================================================================
function AssetsCard() {
  const assets = usePersonalAssets();
  const create = useCreatePersonalAsset();
  const remove = useDeletePersonalAsset();
  const [adding, setAdding] = useState(false);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Home className="h-4 w-4" /> Personal assets
            </CardTitle>
            <CardDescription>
              House, vehicles, brokerage, retirement, HSA, 529. Drives net worth and unlocks
              the &quot;sell vehicle to LLC&quot; §179 play when linked to a debt.
            </CardDescription>
          </div>
          <Button size="sm" variant="primary" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" /> Add asset
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {assets.isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {assets.data && assets.data.length === 0 && !adding && (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            No assets on file. Click <span className="font-semibold">Add asset</span> to get started.
          </div>
        )}
        {assets.data?.map((a) => (
          <AssetRow key={a.id} asset={a} onDelete={() => remove.mutate(a.id)} />
        ))}
        {adding && (
          <AssetRow
            asset={emptyAsset()}
            isNew
            onCancel={() => setAdding(false)}
            onCreate={async (input) => {
              await create.mutateAsync(input);
              setAdding(false);
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function emptyAsset(): PersonalAsset {
  return {
    id: "00000000-0000-0000-0000-000000000000",
    ownerUserId: "",
    name: "",
    type: "Brokerage",
    currentValue: 0,
    purchasePrice: null,
    acquisitionDate: null,
    isTaxAdvantagedAccount: false,
    institution: null,
    vehicleYear: null,
    vehicleMake: null,
    vehicleModel: null,
    vehicleGvwrLb: null,
    estimatedBusinessUsePct: null,
    isBusinessConvertible: false,
    notes: "",
  };
}

function AssetRow({
  asset,
  isNew,
  onDelete,
  onCreate,
  onCancel,
}: {
  asset: PersonalAsset;
  isNew?: boolean;
  onDelete?: () => void;
  onCreate?: (input: Partial<PersonalAsset>) => Promise<void> | void;
  onCancel?: () => void;
}) {
  const update = useUpdatePersonalAsset();
  const [draft, setDraft] = useState<PersonalAsset>(asset);
  const [expanded, setExpanded] = useState(isNew ?? false);
  const [saveFailed, setSaveFailed] = useState(false);

  useEffect(() => {
    setDraft(asset);
    setSaveFailed(false);
  }, [asset]);

  const isVehicle = draft.type === "Vehicle";
  const dirty = JSON.stringify(draft) !== JSON.stringify(asset);

  const setType = (t: PersonalAssetType) =>
    setDraft({
      ...draft,
      type: t,
      isTaxAdvantagedAccount: ASSET_TYPES_TAX_ADV.includes(t),
    });

  const save = async () => {
    setSaveFailed(false);
    try {
      if (isNew && onCreate) {
        await onCreate(draft);
      } else {
        await update.mutateAsync({ ...draft, id: draft.id });
      }
    } catch {
      setSaveFailed(true);
    }
  };

  return (
    <div className="rounded-md border border-border bg-muted/10 p-3">
      <div className="grid grid-cols-12 items-center gap-2">
        <div className="col-span-3">
          <Input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="e.g. 2022 F-150 / Vanguard taxable"
          />
        </div>
        <div className="col-span-3">
          <Select value={draft.type} onChange={(e) => setType(e.target.value as PersonalAssetType)}>
            {Object.entries(assetTypeLabel).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
        </div>
        <div className="col-span-3">
          <Input
            type="number"
            inputMode="decimal"
            step="0.01"
            placeholder="Current value"
            value={draft.currentValue || ""}
            onChange={(e) => setDraft({ ...draft, currentValue: Number(e.target.value) || 0 })}
          />
        </div>
        <div className="col-span-2">
          <Input
            value={draft.institution ?? ""}
            placeholder="Institution"
            onChange={(e) => setDraft({ ...draft, institution: e.target.value || null })}
          />
        </div>
        <div className="col-span-1 flex justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setExpanded((x) => !x)}
            title={expanded ? "Collapse" : "More fields"}
          >
            {expanded ? "−" : "+"}
          </Button>
          {!isNew && onDelete && (
            <Button size="sm" variant="ghost" onClick={onDelete} title="Delete">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-3 grid grid-cols-1 gap-3 border-t border-border pt-3 md:grid-cols-2">
          <NumField
            label="Original purchase price (cost basis)"
            value={draft.purchasePrice}
            onChange={(e) =>
              setDraft({
                ...draft,
                purchasePrice: e.target.value === "" ? null : Number(e.target.value),
              })
            }
          />
          <div className="space-y-1">
            <Label className="text-xs">Acquisition date</Label>
            <Input
              type="date"
              value={draft.acquisitionDate ?? ""}
              onChange={(e) => setDraft({ ...draft, acquisitionDate: e.target.value || null })}
            />
          </div>
          {isVehicle && (
            <>
              <div className="md:col-span-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs">
                <div className="flex items-center gap-2 font-semibold text-emerald-300">
                  <Zap className="h-4 w-4" /> Vehicle / business-convert details
                </div>
                <p className="mt-1 text-emerald-100/80">
                  GVWR &gt; 6,000 lb unlocks the heavy-vehicle §179 cap ($31,300 in 2025). Mark
                  &quot;business-convertible&quot; to model the sell-to-LLC payoff scenario in the plan.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <NumField
                  label="Year"
                  value={draft.vehicleYear}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      vehicleYear: e.target.value === "" ? null : Number(e.target.value),
                    })
                  }
                />
                <div className="space-y-1">
                  <Label className="text-xs">Make</Label>
                  <Input
                    value={draft.vehicleMake ?? ""}
                    onChange={(e) => setDraft({ ...draft, vehicleMake: e.target.value || null })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Model</Label>
                  <Input
                    value={draft.vehicleModel ?? ""}
                    onChange={(e) => setDraft({ ...draft, vehicleModel: e.target.value || null })}
                  />
                </div>
              </div>
              <NumField
                label="GVWR (lb) — door-jamb sticker"
                value={draft.vehicleGvwrLb}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    vehicleGvwrLb: e.target.value === "" ? null : Number(e.target.value),
                  })
                }
              />
              <div className="space-y-1">
                <Label className="text-xs">Estimated business use % (if converted)</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="1"
                  placeholder="e.g. 80"
                  value={
                    draft.estimatedBusinessUsePct == null
                      ? ""
                      : Math.round(draft.estimatedBusinessUsePct * 100)
                  }
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      estimatedBusinessUsePct:
                        e.target.value === "" ? null : Number(e.target.value) / 100,
                    })
                  }
                />
                <p className="text-[11px] text-muted-foreground">
                  Must be &gt;50% to qualify for §179 / bonus depreciation.
                </p>
              </div>
              <label className="md:col-span-2 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.isBusinessConvertible}
                  onChange={(e) => setDraft({ ...draft, isBusinessConvertible: e.target.checked })}
                />
                Mark this vehicle as <span className="font-semibold">business-convertible</span> — the
                §179 sell-to-LLC play will surface on the matching debt.
              </label>
            </>
          )}
          <label className="md:col-span-2 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.isTaxAdvantagedAccount}
              onChange={(e) => setDraft({ ...draft, isTaxAdvantagedAccount: e.target.checked })}
            />
            Tax-advantaged account (Roth / Trad / HSA / 529)
          </label>
          <div className="md:col-span-2 space-y-1">
            <Label className="text-xs">Notes</Label>
            <Input
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            />
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center justify-end gap-2">
        {saveFailed && (
          <span className="mr-auto text-xs text-destructive">
            Asset was not saved. Check the fields and try again.
          </span>
        )}
        {isNew && (
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        {(isNew || dirty) && (
          <Button size="sm" variant="primary" onClick={save}>
            {isNew ? "Add" : "Save"}
          </Button>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// Schedule A simulator card — itemize vs standard, computed from real txns
// =====================================================================
function ScheduleACard() {
  const sim = useScheduleASimulation();
  const data = sim.data;

  if (sim.isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Coins className="h-4 w-4" /> Schedule A simulator
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Calculating itemized vs. standard…
        </CardContent>
      </Card>
    );
  }

  if (data?.blocked || !data?.simulation) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Coins className="h-4 w-4" /> Schedule A simulator
          </CardTitle>
          <CardDescription>
            Itemize vs. standard, computed from your personal-scope transactions.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
            <div className="flex items-center gap-2 font-medium">
              <AlertTriangle className="h-4 w-4" />
              Needs input — {data?.reason ?? "filing status missing"}
            </div>
          </div>
          {data?.routeHint && (
            <Link to={data.routeHint as "/people"}>
              <Button size="sm" variant="primary">
                Open Family Profile <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </Link>
          )}
        </CardContent>
      </Card>
    );
  }

  const s = data.simulation;
  const beats = s.itemizingBeatsStandard;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Coins className="h-4 w-4" /> Schedule A simulator
          {beats ? (
            <Badge tone="success">Itemize wins by {formatMoney(s.itemizingAdvantage)}</Badge>
          ) : (
            <Badge tone="neutral">Standard wins (this year)</Badge>
          )}
        </CardTitle>
        <CardDescription>
          Computed from {s.countedRows} personal-scope transaction{s.countedRows === 1 ? "" : "s"} on your ledger. Tag rows on the Money page as <span className="font-medium text-foreground">Personal</span> to feed this.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Stat label="Itemized total" value={formatMoney(s.itemizedTotal)} tone={beats ? "good" : undefined} />
          <Stat label="Standard deduction" value={formatMoney(s.standardDeduction)} tone={!beats ? "good" : undefined} />
        </div>
        <div className="space-y-1 text-xs">
          <Row k="SALT (property + state income)" v={`${formatMoney(s.saltAfterCap)} of ${formatMoney(s.saltSubTotalUncapped)}`} />
          {s.saltCappedAway > 0 && (
            <Row k="↳ Capped away by $10k SALT cap" v={`-${formatMoney(s.saltCappedAway)}`} tone="bad" />
          )}
          <Row k="Mortgage interest" v={formatMoney(s.mortgageInterest)} />
          <Row k="Medical (above 7.5% AGI floor)" v={`${formatMoney(s.medicalDeductible)} of ${formatMoney(s.medicalRaw)}`} />
          <Row k="Charitable contributions" v={formatMoney(s.charitableContributions)} />
          {s.investmentInterest > 0 && (
            <Row k="Investment interest" v={formatMoney(s.investmentInterest)} />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Row({ k, v, tone }: { k: string; v: string; tone?: "good" | "bad" }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{k}</span>
      <span
        className={
          tone === "good"
            ? "font-medium tabular-nums text-emerald-400"
            : tone === "bad"
              ? "font-medium tabular-nums text-rose-400"
              : "font-medium tabular-nums"
        }
      >
        {v}
      </span>
    </div>
  );
}

// =====================================================================
// BLS CES benchmark — "household at your income spends $X here"
// =====================================================================
function BlsBenchmarkCard() {
  const proj = useTaxProjection();
  const income = usePersonalIncome();
  const agi = proj.data?.projection.agi ?? null;
  const totalNetMonthly = income.data?.totalMonthlyNet ?? null;

  // Prefer engine-projected AGI, fall back to (monthly net × 12) only as a hint.
  const lookupIncome = (agi && agi > 0) ? agi : (totalNetMonthly ? totalNetMonthly * 12 : null);
  const bench = useBlsBenchmark(lookupIncome);

  if (!lookupIncome) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Home className="h-4 w-4" /> BLS spending benchmark
          </CardTitle>
          <CardDescription>
            Average household spend at your income tier (BLS Consumer Expenditure Survey).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
            Needs input — fill in your monthly net or run a tax projection so we can pick a bracket.
          </div>
        </CardContent>
      </Card>
    );
  }

  if (bench.isLoading || !bench.data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Home className="h-4 w-4" /> BLS spending benchmark
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </CardContent>
      </Card>
    );
  }

  const b = bench.data;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Home className="h-4 w-4" /> BLS spending benchmark
          <Badge tone="neutral">{b.incomeBracketLabel}</Badge>
        </CardTitle>
        <CardDescription>
          Average annual spend per household at your income tier (BLS CES, CY2022). Use as a sanity check, not a target.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2 text-xs">
        <Row k="Avg total annual expenditures" v={formatMoney(b.averageAnnualExpenditures)} />
        <Row k="Housing (shelter)" v={formatMoney(b.housingShelter)} />
        <Row k="Utilities" v={formatMoney(b.utilities)} />
        <Row k="Insurance (personal)" v={formatMoney(b.insurance)} />
        <Row k="Transportation" v={formatMoney(b.transportation)} />
        <Row k="Food (at home + away)" v={formatMoney(b.foodAtHome + b.foodAwayFromHome)} />
        <Row k="Healthcare (out-of-pocket)" v={formatMoney(b.healthcareOutOfPocket)} />
        <Row k="Property taxes" v={formatMoney(b.propertyTaxes)} />
        <Row k="Mortgage interest" v={formatMoney(b.mortgageInterest)} />
        <Row k="Charitable contributions" v={formatMoney(b.cashContributions)} />
      </CardContent>
    </Card>
  );
}
